import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Icon,
  SimpleGrid,
  Divider,
  List,
  ListItem,
  ListIcon,
  Grid,
  GridItem,
  Flex,
  Center,
  Badge,
} from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import {
  FiFileText,
  FiMessageSquare,
  FiDatabase,
  FiCpu,
  FiSend,
  FiArrowRight,
  FiCheckCircle,
  FiXCircle,
  FiThumbsUp,
  FiArrowDown,
} from 'react-icons/fi';

// A component for the Before/After cards
const FeatureCard = ({ icon, title, description, colorScheme }) => (
  <Card shadow="lg" textAlign="center" borderTop="4px" borderTopColor={`${colorScheme}.400`} h="full">
    <CardBody py={8}>
      <VStack spacing={4}>
        <Center w={20} h={20} bg={`${colorScheme}.100`} borderRadius="full">
          <Icon as={icon} boxSize={10} color={`${colorScheme}.600`} />
        </Center>
        <Heading size="md" color="gray.800">{title}</Heading>
        <Text color="gray.600" px={4}>{description}</Text>
      </VStack>
    </CardBody>
  </Card>
);

const PayerResponseWorkflow = () => {
  return (
    <Grid
      templateAreas={`"header header" "main main"`}
      gridTemplateRows={'60px 1fr'}
      gridTemplateColumns={'1fr'}
      h='100vh'
      gap='1'
    >
      <HeaderMenus />
      <GridItem area={'main'} bg="gray.50" overflowY="auto">
        <Container maxW="7xl" py={16}>
          <VStack spacing={16} align="stretch">
            <VStack spacing={6} textAlign="center">
              <Badge colorScheme="blue" fontSize="md" px={4} py={2} borderRadius="full">
                Workflow Deep Dive
              </Badge>
              <Heading size="2xl" color="gray.800">How Revedy Builds a Data-Driven Appeal</Heading>
              <Text fontSize="xl" color="gray.600" maxW="4xl">
                Transform the appeal process from a manual struggle into an intelligent, automated workflow that wins.
              </Text>
            </VStack>

            {/* Section 1: Before and After */}
            <VStack spacing={4}>
                <Heading size="xl" color="gray.700">The Manual Way (Before Revedy)</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Time-Consuming Research" description="Manually digging through clinical notes and payer websites." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Confusing Payer Rules" description="Policies are often 100+ pages long and cover many procedures." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Weak, Generic Arguments" description="Using the same template for every appeal is ineffective." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Low Success Rate" description="Payers easily dismiss appeals that aren't specific and evidence-backed." />
                </SimpleGrid>
            </VStack>

            <Flex justify="center" align="center" direction="column">
                <Icon as={FiArrowDown} boxSize={16} color="gray.400" />
                <Text fontWeight="bold" color="blue.600" mt={2}>IS TRANSFORMED INTO</Text>
            </Flex>

            <VStack spacing={4}>
                <Heading size="xl" color="gray.700">The Revedy Way (After Revedy)</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Automated Evidence Gathering" description="The AI instantly analyzes all clinical notes and payer communications." />
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Pinpoint Policy Extraction" description="Our engine finds the exact policy rules relevant to your case, ignoring the noise." />
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Case-Specific Arguments" description="Every appeal is custom-built, quoting the payer's own rules as evidence." />
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Data-Driven Success" description="Appeals are so well-supported and specific that they are hard to deny." />
                </SimpleGrid>
            </VStack>

            <Divider my={8} />

            {/* Section 2: The Revedy Workflow */}
            <VStack spacing={8} align="stretch">
              <Heading size="xl" textAlign="center" color="gray.700">How It Works: From Raw Data to Winning Argument</Heading>
              
              {/* Part A: Inputs */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="blue.600" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={6}>
                  Part A: We Gather the Essential Evidence
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                  <Card shadow="xl" h="full" borderTop="4px" borderColor="blue.400">
                    <CardBody p={6}>
                      <VStack spacing={4} textAlign="center">
                        <Icon as={FiFileText} boxSize={12} color="blue.500" />
                        <Heading size="md">1. Your Clinical Documentation</Heading>
                        <Text fontSize="md">We analyze all case files (Op notes, reports, etc.).</Text>
                        <Badge colorScheme="blue">Output: The "Procedure Context"</Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                  <Card shadow="xl" h="full" borderTop="4px" borderColor="orange.400">
                    <CardBody p={6}>
                      <VStack spacing={4} textAlign="center">
                        <Icon as={FiMessageSquare} boxSize={12} color="orange.500" />
                        <Heading size="md">2. The Payer's Communication</Heading>
                        <Text fontSize="md">We read the denial letter to identify the payer, plan, and state.</Text>
                        <Badge colorScheme="orange">Output: The "Payer Context"</Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                  <Card shadow="xl" h="full" borderTop="4px" borderColor="purple.400">
                    <CardBody p={6}>
                      <VStack spacing={4} textAlign="center">
                        <Icon as={FiDatabase} boxSize={12} color="purple.500" />
                        <Heading size="md">3. Our Payer Policy Library</Heading>
                        <Text fontSize="md">We access our comprehensive library of payer rules and guidelines.</Text>
                        <Badge colorScheme="purple">Output: The "Policy Document"</Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </Box>

              <Flex justify="center" my={4}>
                <Icon as={FiArrowRight} boxSize={16} color="gray.300" transform={{base: "rotate(90deg)", md: "none"}} />
              </Flex>

              {/* Part B: AI Engine */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="blue.600" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={6}>
                  Part B: The Revedy AI Engine Connects the Dots
                </Text>
                <Card bgGradient="linear(to-r, gray.100, blue.50)" shadow="2xl" borderWidth="1px" borderColor="gray.200">
                  <CardBody p={8}>
                    <HStack spacing={8} flexDir={{base: "column", md: "row"}}>
                      <Box p={6} borderRadius="2xl" bgGradient="linear(to-br, blue.400, blue.600)" color="white" shadow="lg">
                        <Icon as={FiCpu} boxSize={16} />
                      </Box>
                      <VStack align="start" spacing={4}>
                        <Text fontSize="md"><Text as="span" fontWeight="bold">1. Define Case:</Text> Uses the "Procedure Context" to understand the specific medical scenario.</Text>
                        <Text fontSize="md"><Text as="span" fontWeight="bold">2. Identify Rules:</Text> Uses the "Payer Context" to select the right "Policy Document".</Text>
                        <Box p={4} bg="teal.50" borderRadius="lg" borderWidth="1px" borderColor="teal.200">
                            <Heading size="sm" color="teal.700">3. EXTRACT RELEVANCE (The Key Step):</Heading>
                            <Text fontSize="md" fontWeight="medium" color="teal.800">The AI reads the entire policy and pulls out **only the specific paragraphs and rules** that apply to this exact case.</Text>
                        </Box>
                      </VStack>
                    </HStack>
                  </CardBody>
                </Card>
              </Box>

              <Flex justify="center" my={4}>
                <Icon as={FiArrowRight} boxSize={16} color="gray.300" transform={{base: "rotate(90deg)", md: "none"}} />
              </Flex>

              {/* Part C: Output */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="blue.600" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={6}>
                  Part C: The Output is a Precise, Evidence-Based Appeal
                </Text>
                <Card bg="white" shadow="2xl" borderWidth="1px" borderColor="gray.200" borderTop="8px" borderTopColor="blue.400">
                  <CardHeader>
                    <HStack>
                      <Icon as={FiSend} boxSize={10} color="blue.600" />
                      <Heading size="lg" color="blue.800">Generated Appeal Letter</Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody px={8} pb={8}>
                    <List spacing={5}>
                      <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Clinical Justification:</Text> Cites evidence directly from your clinical documentation.
                      </ListItem>
                      <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Medical Necessity Argument:</Text> Quotes the payer's own rules back to them using the extracted policy text.
                      </ListItem>
                      <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Evidence Summary:</Text> Lists all sources used to build the argument, creating an audit-proof record.
                      </ListItem>
                    </List>
                  </CardBody>
                </Card>
              </Box>
            </VStack>

            <Divider my={8} />

            {/* Section 3: The Result */}
            <VStack spacing={8}>
                <Heading size="xl" color="gray.700">The Result: A Smarter, Faster, More Effective Process</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Solves Research" description="Automates the gathering of clinical and policy data." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Solves Confusion" description="Pinpoints the exact rules that matter, ignoring the rest." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Solves Generic Arguments" description="Creates a unique, evidence-based appeal for every case." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Drives Success" description="Makes the argument so specific and well-supported it's hard to deny." />
                </SimpleGrid>
            </VStack>

          </VStack>
        </Container>
      </GridItem>
    </Grid>
  );
};

export default PayerResponseWorkflow;